import React, {useEffect, useState} from 'react'
import { OTPForm } from '@/components'
import { useDispatch, useSelector } from 'react-redux';
import { authFailureStatus, authSessionSelector, authSuccessStatus } from '@/services/redux/selectors';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Routes as WebRoutes } from '@/navigation/Routes';
import {useToast} from '@/hooks';
import { ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes } from '@/utils/getMessages';
import { resetAuthMessage, resetAuthSession } from '@/services/redux/slices';

export const OTPScreen: React.FC = () => {
  // ================================
  // hooks
  // ================================
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {showToast} = useToast();
  const location = useLocation();
  // ================================
  // selectors
  // ================================
  const authSession = useSelector(authSessionSelector);
  const authSuccess = useSelector(authSuccessStatus) as SuccessCodes;
  const authFailure = useSelector(authFailureStatus) as ErrorCodes;
  
  // ================================
  // states
  // ================================
  // const [disabled, setDisabled] = useState<boolean>(false);
  const otpType = location.pathname === WebRoutes.VERIFY_OTP ? 'OTP' : 'MFA';
  const authType = authSession?.auth_type;
  const email = authSession?.email;
  const phone = authSession?.phone;
  const originalPhone = authSession?.originalPhone;
  const disabled = authSession?.disabled;

  // ================================
  // functions
  // ================================
  function handlePrevious(){
    // If on MFA screen, navigate back to OTP screen
    if (otpType === 'MFA') {
      navigate(WebRoutes.VERIFY_OTP);
      return;
    }

    // If on OTP screen, navigate back to Login/Signup
    let navigationRoute: string = WebRoutes.LOGIN;
    let navigationState: any = { fromOTP: true };
    
    if (authType === 'signup') {
      navigationRoute = WebRoutes.SIGNUP;
      // Pass form data via navigation state for signup
      navigationState = {
        fromOTP: true,
        formData: {
          email_id: email || '',
          phone: originalPhone || '',
          name: authSession?.formData?.name || '',
          location: authSession?.formData?.location || -1,
        }
      };
    } else if (authType === 'specialist_signup') {
      navigationRoute = WebRoutes.SPECIALIST_SIGNUP;
      // Pass form data via navigation state for specialist signup
      navigationState = {
        fromOTP: true,
        formData: authSession?.formData || {},
        email: email || '',
        phone: originalPhone || '',
      };
    } else if (authType === 'login') {
      // Pass email for login
      navigationState = {
        fromOTP: true,
        email_id: email || '',
      };
    }

    // if disabled due to account block, clear the auth session
    if (disabled){
      dispatch(resetAuthSession());
      navigationState = { fromOTP: true }; // Reset state if session is cleared
    }

    navigate(navigationRoute, { state: navigationState });
  }
  
  function goToMfaVerify(){
    navigate(WebRoutes.VERIFY_MFA);
  }

  // ================================
  // side effects
  // ================================
  useEffect(() => {
    if (authSuccess) {
      showToast(getSuccessMessage(authSuccess), 'success');

      // if mfa is sent, go to mfa screen
      if (authSuccess === 'S-10007') {
        goToMfaVerify();
      }
    }
    if (authFailure) {
      showToast(getErrorMessage(authFailure), 'failure');
    }
    // clean up
    return () => {dispatch(resetAuthMessage())};
  }, [authSuccess, authFailure, goToMfaVerify]);

  // if user is on mfa screen and phone is null that means mfa is not initiated yet (edge case)
  if (otpType === 'MFA' && !phone){
    return <Navigate to={WebRoutes.VERIFY_OTP} />
  }
  // if no auth type, redirect to login
  if (!authType) {
    return <Navigate to={WebRoutes.LOGIN} />
  };

  // if auth type is login, show login form
  return (
    <OTPForm
      email={email}
      phone={phone}
      variant={otpType}
      isSignup={authType === 'signup' || authType === 'specialist_signup'}
      handlePrevious={handlePrevious}
      disabled={disabled}
    />
  )
}